if (typeof ScrollingText !== "function") {
	class ScrollingText extends HTMLElement {
		constructor() {
			super();
			this.position = 0;
			this.isRunning = false;
			this.lastTime = null;
			this.windowInFocus = true;
			this.innerElementTemplate = null;
		}

		connectedCallback() {
			this.init();
		}

		disconnectedCallback() {
			this.stop();
			this.removeEventListeners();
		}

		init() {
			const innerElement = this.children?.[0] || null;
			if (!innerElement) {
				this.isInvalid = true;
				return;
			}

			innerElement.style.position = "relative";

			const rawSpeed = parseInt(this.dataset.scrollingSpeed) || 100;
			this.speed = window.innerWidth > 768 ? rawSpeed : rawSpeed / 1.5;
			this.direction = this.dataset.scrollingDirection || "ltr";
			this.boxWidth = this.getWidth(this);
			this.innerElementWidth = this.getWidth(innerElement);
			this.innerElementTemplate = innerElement.cloneNode(true);

			this.setupChildren();
			this.setupEventListeners();
			this.setupIntersectionObserver();
		}

		getWidth(element) {
			if (!element) return 0;
			const rect = element.getBoundingClientRect();
			return rect.right - rect.left;
		}

		refreshWidths() {
			this.boxWidth = this.getWidth(this);
			this.innerElementWidth = this.getWidth(this.children[0]);
		}

		calculateNumElements() {
			if (!this.innerElementWidth || this.innerElementWidth <= 0) return 1;
			return Math.ceil(this.boxWidth / this.innerElementWidth) + 1;
		}

		setupChildren() {
			const qty = this.calculateNumElements();
			const currentCount = this.children.length;

			if (qty > currentCount) {
				for (let i = currentCount; i < qty; i++) {
					this.appendChild(this.innerElementTemplate.cloneNode(true));
				}
			} else if (qty < currentCount) {
				for (let i = qty; i < currentCount; i++) {
					this.removeChild(this.lastChild);
				}
			}
		}

		nextFrame(delta) {
			this.refreshWidths();
			this.setupChildren();

			Array.from(this.children).forEach((el) => {
				const translateX = this.direction === "rtl" ? this.position : -this.position;
				el.style.transform = `translateX(${translateX}px)`;
			});

			this.position += (this.speed * delta) / 1000;

			if (this.position >= this.innerElementWidth) {
				this.position = this.position % this.innerElementWidth;
			}
		}

		start() {
			if (this.isInvalid || this.isRunning) return;

			this.isRunning = true;
			this.lastTime = null;

			const loop = () => {
				if (!this.isRunning) return;

				const now = Date.now();
				const delta = this.lastTime === null ? 0 : now - this.lastTime;

				this.nextFrame(delta);
				this.lastTime = now;

				window.requestAnimationFrame(loop);
			};

			window.requestAnimationFrame(loop);
		}

		stop() {
			this.isRunning = false;
		}

		setupEventListeners() {
			if (this.dataset.pauseOnHover !== "true") return;

			this.handleWindowBlur = () => {
				this.windowInFocus = false;
			};

			this.handleWindowFocus = () => {
				this.windowInFocus = true;
			};

			this.handleMouseOver = () => {
				if (this.windowInFocus) {
					this.stop();
				}
			};

			this.handleMouseOut = () => {
				if (this.windowInFocus) {
					this.start();
				}
			};

			window.addEventListener("blur", this.handleWindowBlur);
			window.addEventListener("focus", this.handleWindowFocus);
			this.addEventListener("mouseover", this.handleMouseOver);
			this.addEventListener("mouseout", this.handleMouseOut);
		}

		removeEventListeners() {
			if (this.dataset.pauseOnHover !== "true") return;

			window.removeEventListener("blur", this.handleWindowBlur);
			window.removeEventListener("focus", this.handleWindowFocus);
			this.removeEventListener("mouseover", this.handleMouseOver);
			this.removeEventListener("mouseout", this.handleMouseOut);
		}

		setupIntersectionObserver() {
			this.intersectionObserver = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting) {
					this.start();
				} else {
					this.stop();
				}
			});

			this.intersectionObserver.observe(this);
		}
	}

	if (typeof customElements.get("scrolling-text") === "undefined") {
		customElements.define("scrolling-text", ScrollingText);
	}
}
